import React, { useState, useEffect } from 'react';
import { dashboardApi } from '../api';
import { Card, PageTitle, Badge, StatCard, Spinner, Btn } from '../components/common/UI';
import { Activity, Server, RefreshCw, HardDrive, Cpu, Wifi } from 'lucide-react';
import toast from 'react-hot-toast';

function fmtBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
}

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (quiet) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await dashboardApi.systemHealth();
      setHealth(data.data);
    } catch (_e) {
      toast.error('Failed to load health data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    const interval = setInterval(() => load(true), 15000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (!health) return null;

  const mem = health.system ? health.system.memory : null;
  const heapUsedPct = mem ? Math.round((mem.heapUsed / mem.heapTotal) * 100) : 0;
  const ramUsedPct = health.system ? Math.round(((health.system.totalmem - health.system.freemem) / health.system.totalmem) * 100) : 0;
  const uptimeDays = health.system ? (health.system.uptime / 86400).toFixed(1) : 0;
  const cpuLoad = health.system && health.system.cpu && health.system.cpu[0] ? (health.system.cpu[0] * 100).toFixed(1) : '0';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <PageTitle sub={'Response: ' + health.responseTime + ' · Updated: ' + new Date(health.timestamp).toLocaleTimeString()}>
          System Health
        </PageTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge color={health.status === 'healthy' ? 'green' : 'red'}>{health.status ? health.status.toUpperCase() : 'UNKNOWN'}</Badge>
          <Btn size="sm" variant="secondary" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? <Spinner size={13} /> : <RefreshCw size={13} />}
          </Btn>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Uptime" value={uptimeDays + 'd'} icon={Server} color="#3b82f6" />
        <StatCard label="CPU Load" value={cpuLoad + '%'} icon={Cpu} color={parseFloat(cpuLoad) > 80 ? '#ef4444' : '#25d366'} sub={(health.system ? health.system.cpus : 0) + ' cores'} />
        <StatCard label="Heap Used" value={heapUsedPct + '%'} icon={HardDrive} color={heapUsedPct > 80 ? '#ef4444' : '#f59e0b'} sub={mem ? (fmtBytes(mem.heapUsed) + ' / ' + fmtBytes(mem.heapTotal)) : ''} />
        <StatCard label="RAM Used" value={ramUsedPct + '%'} icon={Activity} color={ramUsedPct > 80 ? '#ef4444' : '#25d366'} sub={health.system ? fmtBytes(health.system.totalmem - health.system.freemem) : ''} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Services</h3>
          {[
            ['Database', health.database ? health.database.status : null, health.database ? health.database.name : null],
            ['WhatsApp', health.whatsapp ? health.whatsapp.status : null, health.whatsapp && health.whatsapp.sessionId ? health.whatsapp.sessionId.slice(-8) : null],
            ['Redis', health.services && health.services.redis ? health.services.redis.status : null, null],
            ['Email', health.services && health.services.email ? health.services.email.status : null, health.services && health.services.email ? health.services.email.email : null],
          ].map((item) => (
            <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #334155' }}>
              <div>
                <span style={{ color: '#e2e8f0', fontSize: 14 }}>{item[0]}</span>
                {item[2] && <p style={{ color: '#64748b', fontSize: 11 }}>{item[2]}</p>}
              </div>
              <Badge color={item[1] === 'connected' || item[1] === 'configured' ? 'green' : item[1] === 'disconnected' ? 'red' : 'default'}>
                {item[1] || 'unknown'}
              </Badge>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Queue Status</h3>
          {[
            ['Waiting', health.queue ? health.queue.waiting : null, '#f59e0b'],
            ['Active', health.queue ? health.queue.active : null, '#3b82f6'],
            ['Completed', health.queue ? health.queue.completed : null, '#25d366'],
            ['Failed', health.queue ? health.queue.failed : null, '#ef4444'],
          ].map((item) => (
            <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #334155' }}>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>{item[0]}</span>
              <span style={{ color: item[2], fontSize: 16, fontWeight: 700 }}>{item[1] !== null ? item[1].toLocaleString() : '—'}</span>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Memory Breakdown</h3>
          {mem && [
            ['RSS', fmtBytes(mem.rss)],
            ['Heap Total', fmtBytes(mem.heapTotal)],
            ['Heap Used', fmtBytes(mem.heapUsed)],
            ['External', fmtBytes(mem.external)],
          ].map((item) => (
            <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #334155' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>{item[0]}</span>
              <span style={{ color: '#e2e8f0', fontSize: 13 }}>{item[1]}</span>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Alerts & System</h3>
          {[
            ['Recent Errors', health.alerts ? health.alerts.recentErrors : 0],
            ['Warning', health.alerts ? (health.alerts.warning || 'None') : 'None'],
            ['Platform', health.system ? health.system.platform : '—'],
            ['Free RAM', health.system ? fmtBytes(health.system.freemem) : '—'],
          ].map((item) => (
            <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #334155' }}>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>{item[0]}</span>
              <span style={{ color: '#e2e8f0', fontSize: 13 }}>{item[1]}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
